import React, { useMemo, useState } from "react";
import { MoreVertical, Printer, Search } from "lucide-react";

const HolidayStructureTab = ({
  darkMode,
  holidayPolicies,
  holidayBank,
  fetchAllMasterData,
  setShowHolidayModal,
  setShowHolidayImportModal,
  setShowHolidayBankModal,
  handleEditHolidayPolicy,
  handleDeleteHolidayPolicy,
  handleAddHolidayFromBank,
  handleRemoveFromHolidayBank,
  formatDate,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openActionFor, setOpenActionFor] = useState(null);
  const [showHolidayBankPanel, setShowHolidayBankPanel] = useState(false);
  const [holidayBankSearch, setHolidayBankSearch] = useState("");
  const [openBankActionFor, setOpenBankActionFor] = useState(null);
  const [deactivatedHolidayIds, setDeactivatedHolidayIds] = useState(() => new Set());
  const filteredPolicies = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return holidayPolicies;
    return holidayPolicies.filter((policy) =>
      String(policy?.name || "").toLowerCase().includes(q)
    );
  }, [holidayPolicies, searchTerm]);
  const filteredHolidayBank = useMemo(() => {
    const q = holidayBankSearch.trim().toLowerCase();
    if (!q) return holidayBank;
    return holidayBank.filter((holiday) => {
      const name = String(holiday?.name || "").toLowerCase();
      const date = String(holiday?.date || "").toLowerCase();
      return name.includes(q) || date.includes(q);
    });
  }, [holidayBank, holidayBankSearch]);
  const isHolidayDeactivated = (id) => deactivatedHolidayIds.has(id);
  const toggleHolidayDeactivated = (id) => {
    setDeactivatedHolidayIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border ${darkMode ? "border-[#3C3C3C] bg-[#1f1f1f]" : "border-gray-200 bg-white"}`}>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-inherit p-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowHolidayBankPanel(true)}
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                darkMode
                  ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]"
                  : "border-gray-300 bg-gray-50 text-gray-700"
              }`}
            >
              Holiday Bank
            </button>
            <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}>Holiday Policies</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search policy..."
                className={`h-9 w-56 rounded-md border pl-8 pr-2 text-sm ${darkMode ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]" : "border-gray-300 bg-white text-gray-800"}`}
              />
            </div>
            <button type="button" className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm">
              <Printer className="h-4 w-4" />
              Print All
            </button>
            <button
              onClick={async () => {
                await fetchAllMasterData();
                setShowHolidayModal(true);
              }}
              className="rh-btn-primary rounded px-3 py-1.5 text-sm hover:opacity-95"
            >
              Create Holiday Policy
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredPolicies.length === 0 ? (
            <p className={`p-3 text-center text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              No holiday policies created yet
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className={darkMode ? "bg-[#2C2C2C]" : "bg-gray-50"}>
                <tr>
                  <th className="px-3 py-2 text-left">Policy</th>
                  <th className="px-3 py-2 text-left">Holidays</th>
                  <th className="px-3 py-2 text-left">Effective</th>
                  <th className="px-3 py-2 text-left">HR Policy Document</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPolicies.map((policy) => (
                  <tr key={policy.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-gray-200"}>
                    <td className="px-3 py-2 font-medium">{policy.name}</td>
                    <td className="px-3 py-2">{policy.holidays?.length || 0}</td>
                    <td className="px-3 py-2">{policy.effectiveDate ? formatDate(policy.effectiveDate) : "-"}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">Policy Preview</td>
                    <td className="relative px-3 py-2 text-right">
                      <button type="button" onClick={() => setOpenActionFor((v) => (v === policy.id ? null : policy.id))} className="inline-flex h-8 w-8 items-center justify-center rounded-md border">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openActionFor === policy.id ? (
                        <div className={`absolute right-3 z-10 mt-1 w-28 rounded-md border shadow-md ${darkMode ? "border-[#3C3C3C] bg-[#2C2C2C]" : "border-gray-200 bg-white"}`}>
                          <button onClick={() => { handleEditHolidayPolicy(policy); setOpenActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs hover:bg-black/5">Edit</button>
                          <button onClick={() => { handleDeleteHolidayPolicy(policy.id); setOpenActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-black/5">Delete</button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showHolidayBankPanel ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-5xl rounded-lg border shadow-xl ${darkMode ? "border-[#3C3C3C] bg-[#1f1f1f] text-[#E0E0E0]" : "border-gray-200 bg-white text-gray-800"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-inherit p-3">
              <h3 className="text-base font-semibold">Holiday Bank</h3>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={holidayBankSearch}
                    onChange={(e) => setHolidayBankSearch(e.target.value)}
                    placeholder="Search holiday..."
                    className={`h-9 w-56 rounded-md border pl-8 pr-2 text-sm ${darkMode ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]" : "border-gray-300 bg-white text-gray-800"}`}
                  />
                </div>
                <button onClick={() => setShowHolidayImportModal(true)} className="rh-btn-primary rounded px-3 py-1.5 text-sm hover:opacity-95">Import</button>
                <button onClick={() => setShowHolidayBankModal(true)} className="rh-btn-primary rounded px-3 py-1.5 text-sm hover:opacity-95">Add</button>
                <button type="button" onClick={() => setShowHolidayBankPanel(false)} className="rounded-md border px-3 py-1.5 text-sm">Close</button>
              </div>
            </div>
            <div className="max-h-[65vh] overflow-auto">
              {filteredHolidayBank.length === 0 ? (
                <p className={`p-6 text-center text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No holidays in bank yet</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className={darkMode ? "bg-[#2C2C2C]" : "bg-gray-50"}>
                    <tr>
                      <th className="px-3 py-2 text-left">Holiday</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHolidayBank.map((holiday) => (
                      <tr key={holiday.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t border-gray-200"}>
                        <td className="px-3 py-2 font-medium">{holiday.name}</td>
                        <td className="px-3 py-2">{formatDate(holiday.date)}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs ${isHolidayDeactivated(holiday.id) ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {isHolidayDeactivated(holiday.id) ? "Deactivated" : "Active"}
                          </span>
                        </td>
                        <td className="relative px-3 py-2 text-right">
                          <button type="button" onClick={() => setOpenBankActionFor((v) => (v === holiday.id ? null : holiday.id))} className="inline-flex h-8 w-8 items-center justify-center rounded-md border">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openBankActionFor === holiday.id ? (
                            <div className={`absolute right-3 z-10 mt-1 w-40 rounded-md border shadow-md ${darkMode ? "border-[#3C3C3C] bg-[#2C2C2C]" : "border-gray-200 bg-white"}`}>
                              <button onClick={() => { handleAddHolidayFromBank(holiday); setOpenBankActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs hover:bg-black/5">Edit</button>
                              <button onClick={() => { toggleHolidayDeactivated(holiday.id); setOpenBankActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs hover:bg-black/5">
                                {isHolidayDeactivated(holiday.id) ? "Activate" : "Deactivate"}
                              </button>
                              <button onClick={() => { handleRemoveFromHolidayBank(holiday.id); setOpenBankActionFor(null); }} className="block w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-black/5">Delete</button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HolidayStructureTab;
